import { Link, useParams } from "react-router-dom"
import { date } from "@churchcenter/datetime-fmt"
import moment from "moment"
import {
  BreadcrumbNavigation,
  BreadcrumbContent,
  BreadcrumbPage,
  BreadcrumbDividerIcon,
} from "source/shared/components"
import { useApiRead } from "source/shared/SessionApiResource"
import { EventDatetimeDisplay } from "source/shared/DatetimeFmt"
import { getRelationship } from "source/shared/getRelationship"
import { LocationDetails } from "source/groups/components/LocationDetails"
import { Heading, Subheading } from "@planningcenter/doxy-web"
import { object, array } from "prop-types"
import BlankState from "source/groups/BlankState"
import { Loading } from "source/shared/components"
export default function EventsShow() {
  const { data: event, included, errors } = useEvent()

  if (errors) {
    return (
      <BlankState icon="churchCenter#groups-o">
        <p className="mb-2">This event is currently unavailable</p>
        <Link to="/groups" className="mr-1 minor-btn secondary-btn btn">
          View all Groups
        </Link>
      </BlankState>
    )
  }

  if (!event) {
    return <Loading />
  }

  return (
    <>
      <BreadcrumbNav {...{ event, included }} />
      <Event {...{ event, included }} />
    </>
  )
}

function BreadcrumbNav({ event, included }) {
  const { name: eventName } = event.attributes
  const group = getRelationship({ data: event, included }, "group")
  const { church_center_web_url, name: groupName } = group.attributes
  const relativeGroupLink = new URL(church_center_web_url).pathname

  return (
    <BreadcrumbNavigation>
      <BreadcrumbPage linkUrl={relativeGroupLink} pageText={groupName} />
      <BreadcrumbDividerIcon />
      <BreadcrumbContent isActive>{eventName}</BreadcrumbContent>
    </BreadcrumbNavigation>
  )
}

BreadcrumbNav.propTypes = {
  event: object.isRequired,
  included: array.isRequired,
}

function Event({ event, included }) {
  const location = getRelationship({ data: event, included }, "location")
  const {
    starts_at,
    ends_at,
    canceled_at,
    name,
    description,
    location_type_preference,
    virtual_location_url,
  } = event.attributes

  const [shortMonthName, dayNumber] = date(starts_at, {
    style: "abbreviated",
  }).split(" ")
  const eventIsCanceled = canceled_at && moment(canceled_at).isBefore(moment())

  return (
    <>
      <header className="d-f fd-cr fd-r@xs mb-3 pt-1 ai-c@xs g-2">
        <div className="f-1">
          <div className="d-f ai-c">
            <Heading level={1} size={2} text={name} />
            {eventIsCanceled && (
              <div className="badge danger-badge ml-1">Canceled</div>
            )}
          </div>
          <div className="mb-4p c-tint2">
            <EventDatetimeDisplay startsAt={starts_at} endsAt={ends_at} />
          </div>
        </div>

        <div className="w-100% w-40%@xs aspect-16-9">
          {event.attributes.image?.medium != null ? (
            <img
              src={event.attributes.image.medium}
              className="w-100% h-a br-8p d-b"
              alt={event.attributes.name}
            />
          ) : (
            <div className="ta-c event-cal h-100% w-100% br-8p">
              <div className="event-cal__month fs-3">{shortMonthName}</div>
              <div className="event-cal__day fs-0">{dayNumber}</div>
            </div>
          )}
        </div>
      </header>

      {description && (
        <div className="mb-3">
          <div className="mb-1">
            <Subheading level={2} text="Details" />
          </div>
          <div data-preformatted-content="heading-scale:legacy-church-center">
            <div dangerouslySetInnerHTML={{ __html: description }} />
          </div>
        </div>
      )}

      <div className="mb-3 w-33%@md">
        <LocationDetails
          preference={location_type_preference}
          virtual={{ url: virtual_location_url }}
          physical={location.attributes}
        />
      </div>
    </>
  )
}

Event.propTypes = {
  event: object.isRequired,
  included: array,
}

function useEvent() {
  const { eventId } = useParams()
  return useApiRead(
    `/groups/v2/events/${eventId}?include=group,location&fields[Group]=church_center_web_url,name`,
  )
}
